<template>
  <q-table
    :rows="filteredAuditLog"
    :columns="visibleColumns"
    :loading="isLoadingAuditLog"
    bordered
    flat
    row-key="id"
    virtual-scroll

    :rows-per-page-options="[0]"
    :class="{
      'virtual-scroll-height': !isAuditLogEmpty,
    }"
    class="sticky-last-column"
    @row-click="onViewAuditLogItem"
  >
    <template #top>
      <div class="row col-sm col-xs-12 q-col-gutter-xs">
        <filter-chips-bar
          :filters="filters"
          :filter-criteria="editableFilterCriteria"
          :locked-filter-keys="lockedFilters"
          :readonly-filter-keys="frozenFilterKeys"
          @update:filters="onFiltersChanged"
        />
      </div>
    </template>
    <template #header-cell-actions="props">
      <q-th :props="props">
        <q-btn
          flat
          round
          dense
          size="sm"
          color="dark-variant"
          icon="settings"
          @click="onSelectColumns"
        />
      </q-th>
    </template>
    <template
      v-if="hasMoreDocs"
      #bottom
    >
      <div class="row full-width justify-center">
        <q-btn
          flat
          color="primary"
          :label="$t('auditLogReports.loadMoreRows')"
          @click="onLoadMoreRows"
        />
      </div>
    </template>
  </q-table>
</template>

<script>
import { filterPropertyTypes } from 'boot/globals';
import ViewAuditEvent from 'components/Audit/ViewAuditEvent';
import FilterChipsBar from 'components/UI/FilterChipsBar.vue';
import { Timestamp } from 'firebase/firestore';
import {
  chain, chunk, cloneDeep, concat, difference, find, get,
  includes, isArray, isEmpty, isEqual, map, orderBy, pick, size, uniqBy,
} from 'lodash';
import { mapState } from 'pinia';
import { useTime } from 'src/composables/time';
import AuditLogDB from 'src/fb/auditLog-db';
import { markRaw } from 'vue';
import UsersDB from 'src/fb/users-db';
import AuditLogMixin from 'src/mixins/AuditLogMixin';
import FiltersMixin from 'src/mixins/FiltersMixin';
import Interactions from 'src/mixins/Interactions';
import { auth } from 'src/store/auth';
import { isSystemActor } from 'src/utils/AuditLogUtils';

const MAX_DISJUNCTION_PER_FIRESTORE_QUERY = 30;
// cells are CSS-truncated to 300px; capping the text keeps full session-note bodies out of the DOM
const MAX_CELL_TEXT_LENGTH = 50;
const EVENT_KIND_FILTER = 'eventKind';

const withoutEventKind = ({ [EVENT_KIND_FILTER]: eventKind, ...filters }) => filters;

export default {
  name: 'AuditTrailTable',

  components: { FilterChipsBar },

  mixins: [AuditLogMixin, FiltersMixin, Interactions],

  props: {
    initialFilters: {
      type: Object,
      default: () => ({}),
    },
    lockedFilters: {
      type: Array,
      default: () => ([]),
    },
  },

  emits: ['filter-changed'],

  setup() {
    const { getLocaleString } = useTime();
    return { getLocaleString };
  },

  data() {
    return {
      isLoadingAuditLog: false,

      auditLog: [],
      lastDoc: null,
      lastDocs: {}, // For chunked queries pagination: { [chunkIndex]: lastDoc }
      filters: {},

      maxRows: 500,
      hasMoreDocs: false,

      availableFilterCriteria: [
        {
          value: 'createTimestamp',
          filterProperty: 'createTimestamp',
          label: this.$t('common.date'),
          filterType: filterPropertyTypes.Date,
          filterPrefix: null,
          options: {
            filterCriteriaItems: [],
          },
        },
        {
          value: 'client',
          filterProperty: 'client',
          label: this.$t('auditLogReports.tableCols.client'),
          filterType: filterPropertyTypes.Client,
          options: {
            filterCriteriaItems: [],
          },
        },
        {
          value: 'user',
          filterProperty: 'user',
          label: this.$t('auditLogReports.tableCols.user'),
          filterType: filterPropertyTypes.User,
          options: {
            filterCriteriaItems: [],
          },
        },
        {
          value: 'collection',
          filterProperty: 'collection',
          label: this.$t('auditLogReports.tableCols.collection'),
          filterType: filterPropertyTypes.StringInArray,
          options: {
            filterCriteriaItems: map(
              this.$tm('auditLogReports.resourceTypes'),
              (label, value) => ({
                value,
                label,
              }),
            ),
          },
        },
        {
          value: 'resourceId',
          filterProperty: 'resourceId',
          label: this.$t('auditLogReports.tableCols.resourceId'),
          filterType: filterPropertyTypes.StringMatch,
          options: {
            filterCriteriaItems: [],
            allowedCriteriaTypes: ['isEqualTo'],
          },
        },
      ],

      selectedColumns: ['createTimestamp', 'collection', 'description'],
    };
  },

  computed: {
    ...mapState(auth, ['userDetails']),

    /*
     * The date range bounds the query and may be re-pointed, never dropped. Every other locked
     * filter names the record this view is about, so it is frozen entirely.
     */
    frozenFilterKeys() {
      return this.lockedFilters.filter((filterKey) => filterKey !== 'createTimestamp');
    }, // frozenFilterKeys()

    /*
     * A resource id cannot be chosen until a collection narrows what it could refer to. Declared as
     * a dependency rather than disabling the row, because this reads the committed filters and the
     * dialog commits nothing until Apply: a collection chosen in the same visit would leave the row
     * disabled until the user applied, reopened, and came back for it.
     */
    editableFilterCriteria() {
      const allFilterCriteria = this.eventKindFilterCriteria
        ? [...this.availableFilterCriteria, this.eventKindFilterCriteria]
        : this.availableFilterCriteria;

      return allFilterCriteria.map((filterCriteria) => {
        if (filterCriteria.value !== 'resourceId') {
          return filterCriteria;
        }

        return {
          ...filterCriteria,
          options: {
            ...(filterCriteria.options ?? {}),
            /*
             * No isSet: it would be read from the committed filters, which is the very thing that
             * made this a disabled row. The dialog checks its own draft, so a collection chosen in
             * this visit counts, and one cleared in this visit stops counting.
             */
            filterDependency: {
              value: 'collection',
              bannerMessage: this.$t('auditLogReports.resourceIdFilterTooltip'),
            },
            tooltipText: this.$t('auditLogReports.resourceIdFilterTooltip'),
          },
        };
      });
    }, // editableFilterCriteria()

    isActivityHistory() {
      return this.lockedFilters.includes('resourceId')
        && isEqual(this.getFilterItems('collection'), ['sessionActivities']);
    }, // isActivityHistory()

    eventKindFilterCriteria() {
      if (!this.isActivityHistory) {
        return null;
      }

      const chosenKinds = this.filters[EVENT_KIND_FILTER]?.filterStatements?.[0]?.filteredItems ?? [];

      return {
        value: EVENT_KIND_FILTER,
        filterProperty: EVENT_KIND_FILTER,
        label: this.$t('auditLogReports.tableCols.eventType'),
        filterType: filterPropertyTypes.StringInArray,
        options: {
          filterCriteriaItems: this.getOfferedAuditEventKinds(this.auditLog, chosenKinds).map((kind) => ({
            value: kind,
            label: this.getAuditEventKindLabel(kind, 'sessionActivities'),
          })),
        },
      };
    }, // eventKindFilterCriteria()

    isAuditLogEmpty() {
      return this.filteredAuditLog.length === 0;
    }, // isAuditLogEmpty

    filteredAuditLog() {
      // date, user, and client filters are applied at query level
      const clientSideFilters = cloneDeep(this.filters);
      delete clientSideFilters.createTimestamp;
      delete clientSideFilters.user;
      delete clientSideFilters.client;
      delete clientSideFilters[EVENT_KIND_FILTER];

      const auditLog = this.getFilteredElements(this.auditLog, clientSideFilters);
      const eventKindStatement = this.filters[EVENT_KIND_FILTER]?.filterStatements?.[0];

      if (!eventKindStatement?.filteredItems?.length) {
        return auditLog;
      }

      return auditLog.filter((auditLogEvent) => (
        eventKindStatement.filteredItems.includes(this.getAuditEventKind(auditLogEvent))
      ) !== Boolean(eventKindStatement.filterNegative));
    },

    fromTime() {
      const fromDate = chain(this.filters)
        .get(['createTimestamp', 'filterStatements'])
        .head()
        .get('fromDate')
        .value();

      if (fromDate instanceof Timestamp) {
        return fromDate;
      }

      return null;
    }, // fromTime()

    toTime() {
      const toDate = chain(this.filters)
        .get(['createTimestamp', 'filterStatements'])
        .head()
        .get('toDate')
        .value();
      if (toDate instanceof Timestamp) {
        return toDate;
      }

      return null;
    }, // toTime()

    visibleColumns() {
      return chain(this.columns)
        .filter((column) => (includes(this.selectedColumns, column.name) || column.persistent))
        .value();
    },

  },

  watch: {
    initialFilters: {
      immediate: true,
      deep: true,
      handler(newFilter) {
        this.filters = cloneDeep(newFilter);
        if (!isEmpty(this.filters)) {
          this.loadAuditLog(true);
        }
      },
    },

    selectedColumns: {
      immediate: false,
      deep: true,
      async handler(newSelectedColumns) {
        if (!newSelectedColumns.includes('user')) {
          return;
        }

        // All loaded rows: clearing the event filter shows the hidden ones without a reload.
        await this.refreshUserCacheFromTable(this.auditLog);
        this.$forceUpdate();
      },
    },
  },

  created() {
    // non-reactive: accessed in format functions during render, must not create reactive dependencies
    this.userCache = [];
    this.cacheReads = [];
    this.columns = [
      {
        name: 'createTimestamp',
        field: 'createTimestamp',
        required: true,
        label: this.$t('common.date'),
        align: 'left',
        sortable: false,
        format: (val) => {
          if (val instanceof Timestamp) {
            return this.getLocaleString(val);
          }
          return '';
        },
      },
      {
        name: 'collection',
        field: 'collection',
        required: true,
        label: this.$t('auditLogReports.tableCols.eventType'),
        align: 'left',
        sortable: false,
        format: (val, auditLogEvent) => this.getAuditEventType(auditLogEvent),
      },
      {
        name: 'description',
        field: 'resourceId',
        required: true,
        label: this.$t('auditLogReports.tableCols.description'),
        align: 'left',
        sortable: false,
        classes: 'truncate-label-500-no-hover',
        style: 'white-space: pre;',
        format: (val, auditLogEvent) => this.getAuditEventLabel(auditLogEvent),
      },
      {
        name: 'user',
        field: 'user',
        required: true,
        label: this.$t('auditLogReports.tableCols.user'),
        align: 'left',
        sortable: false,
        format: (val, auditLogEvent) => (this.formatAuditEventField(auditLogEvent, 'user', this.userCache)),
      },
      {
        name: 'resourceId',
        field: 'resourceId',
        required: true,
        label: this.$t('auditLogReports.tableCols.resourceId'),
        align: 'left',
        sortable: false,
      },
      {
        name: 'field',
        field: 'field',
        required: true,
        label: this.$t('auditLogReports.tableCols.field'),
        align: 'left',
        sortable: false,
        format: (val, auditLogEvent) => this.formatAuditEventField(auditLogEvent, 'field', this.userCache),
      },
      {
        name: 'oldValue',
        field: 'oldValue',
        required: true,
        label: this.$t('auditLogReports.tableCols.oldValue'),
        align: 'left',
        sortable: false,
        classes: 'truncate-label-300-no-hover',
        format: (val, auditLogEvent) => this.formatAuditLogValue(
          this.formatAuditEventField(auditLogEvent, 'oldValue', this.userCache),
          MAX_CELL_TEXT_LENGTH,
        ),
      },
      {
        name: 'newValue',
        field: 'newValue',
        required: true,
        label: this.$t('auditLogReports.tableCols.newValue'),
        align: 'left',
        sortable: false,
        classes: 'truncate-label-300-no-hover',
        format: (val, auditLogEvent) => this.formatAuditLogValue(
          this.formatAuditEventField(auditLogEvent, 'newValue', this.userCache),
          MAX_CELL_TEXT_LENGTH,
        ),
      },
      {
        name: 'actions',
        label: null,
        field: '',
        align: 'right',
        sortable: false,
        hideFromSelection: true,
        persistent: true,
      },
    ];

    const auditTrailTableSelectedColumns = localStorage.getItem('auditTrailTableSelectedColumns');
    if (auditTrailTableSelectedColumns !== null) {
      this.selectedColumns = JSON.parse(auditTrailTableSelectedColumns);
    }
  },

  methods: {

    onSelectColumns() {
      const selectionColumns = chain(this.columns)
        .reject({ hideFromSelection: true })
        .map((column) => ({ value: column.name, label: column.label, disable: column.mandatory }))
        .value();
      this.$q.dialog({
        title: this.$t('common.tableEditColumns'),
        options: {
          type: 'checkbox',
          model: this.selectedColumns,
          items: selectionColumns,
        },
        cancel: true,
        persistent: true,

      }).onOk((newSelectedColumns) => {
        this.selectedColumns = newSelectedColumns;
        if (isArray(newSelectedColumns)) {
          localStorage.setItem('auditTrailTableSelectedColumns', JSON.stringify(newSelectedColumns));
        }
      });
    }, // onSelectColumns()

    onLoadMoreRows() {
      this.loadAuditLog(false);
    },

    async onViewAuditLogItem(evt, row) {
      if (row.user) {
        await this.refreshUserCache([row.user]);
      }

      this.openComponentDialog(ViewAuditEvent, {
        auditLogEvent: row,
        userCache: this.userCache,
      });
    },

    /*
     * A resource id means nothing without the collection it belongs to, so it goes whenever the
     * collection does. Checked against the resulting map rather than against which chip was
     * removed, so clearing everything at once cleans up too.
     */
    async onFiltersChanged(filters) {
      const orphanedResourceId = !('collection' in filters) && 'resourceId' in filters;
      const previousFilters = this.filters;

      this.filters = orphanedResourceId
        ? Object.fromEntries(Object.entries(filters).filter(([key]) => key !== 'resourceId'))
        : filters;

      this.$emit('filter-changed', this.filters);

      const isEventKindOnlyChange = !isEqual(previousFilters[EVENT_KIND_FILTER], this.filters[EVENT_KIND_FILTER])
        && isEqual(withoutEventKind(previousFilters), withoutEventKind(this.filters));

      if (isEventKindOnlyChange) {
        return;
      }

      await this.loadAuditLog(true);
    }, // onFiltersChanged()

    /**
     * Loads (or appends, on "load more") audit log entries for the current filters.
     *
     * Builds the shared base constraints once, then splits the user/client filters into
     * Firestore-safe chunks and runs the chunk combinations in parallel.
     *
     * @param {boolean} [reset=false] When true, clears pagination cursors and replaces the
     *   current log; otherwise appends the next page.
     * @return {Promise<void>}
     */
    async loadAuditLog(reset = false) {
      this.isLoadingAuditLog = true;
      if (reset) {
        this.lastDocs = {};
      }

      const baseConstraints = this.buildBaseConstraints();
      const { userChunks, clientChunks } = this.buildFilterChunks(
        this.getFilterItems('user'),
        this.getFilterItems('client'),
        this.getFilterItems('collection'),
      );

      try {
        const auditLogDocs = await this.executeChunkedQueries(baseConstraints, userChunks, clientChunks, reset);

        // populate user cache before setting auditLog so format functions have names on first render
        if (this.selectedColumns.includes('user')) {
          await this.refreshUserCacheFromTable(auditLogDocs);
        }

        if (reset) {
          this.auditLog = auditLogDocs;
        } else {
          this.auditLog = concat(this.auditLog, auditLogDocs);
        }
      } finally {
        this.isLoadingAuditLog = false;
      }
    }, // loadAuditLog()

    /**
     * Builds the constraints shared by every audit log query and that need no chunking:
     * clinic, date range, and collection (plus resourceId, which depends on collection).
     *
     * @return {Array<Constraint>}
     */
    buildBaseConstraints() {
      const constraints = [['clinic', '==', this.userDetails.clinic]];

      if (this.fromTime !== null) {
        constraints.push(['createTimestamp', '>=', this.fromTime]);
      }
      if (this.toTime !== null) {
        constraints.push(['createTimestamp', '<=', this.toTime]);
      }

      const collectionItems = this.getFilterItems('collection');
      if (!isEmpty(collectionItems) && size(collectionItems) <= MAX_DISJUNCTION_PER_FIRESTORE_QUERY) {
        constraints.push(['collection', 'in', collectionItems]);

        // resourceId filter only works when collection filter is also applied (index limitation)
        // resourceId is a StringMatch filter, so getFilterItems returns the raw string value
        const resourceIdFilter = this.getFilterItems('resourceId');
        if (!isEmpty(resourceIdFilter)) {
          constraints.push(['resourceId', '==', resourceIdFilter]);
        }
      }

      return constraints;
    },

    /**
     * Splits the user and client filters into chunks small enough that any single query
     * stays within the Firestore disjunctions (DNF) limit.
     *
     * Firestore allows at most {@link MAX_DISJUNCTION_PER_FIRESTORE_QUERY} disjunctions per
     * query, counted as the product of every `in` clause size. The base constraints already
     * contribute a `collection in [...]` clause, so the budget left for the user/client `in`
     * clauses is `floor(limit / collectionCount)`.
     *
     * Only when BOTH user and client are selected AND the full product
     * (collection × user × client) would exceed the limit do we query the smaller set one
     * value at a time (`==`, factor of 1) while chunking the larger set with `in`. Otherwise
     * each present filter is chunked normally; an absent filter yields a single empty chunk
     * (`[[]]`) so the cartesian product in executeChunkedQueries still issues one query.
     *
     * @param {Array<string>} userFilters Selected user filter values.
     * @param {Array<string>} clientFilters Selected client filter values.
     * @param {Array<string>} collectionItems Selected collection filter values (already in baseConstraints).
     * @return {{ userChunks: string[][], clientChunks: string[][] }}
     */
    buildFilterChunks(userFilters, clientFilters, collectionItems) {
      const disjunctionsQtty = (collectionItems.length || 1) * (userFilters.length || 1) * (clientFilters.length || 1);
      const chunkSize = collectionItems.length
        ? Math.floor(MAX_DISJUNCTION_PER_FIRESTORE_QUERY / collectionItems.length)
        : MAX_DISJUNCTION_PER_FIRESTORE_QUERY;

      const exceedsLimit = disjunctionsQtty > MAX_DISJUNCTION_PER_FIRESTORE_QUERY;
      const hasBothFilters = size(userFilters) > 0 && size(clientFilters) > 0;

      if (exceedsLimit && hasBothFilters) {
        if (userFilters.length <= clientFilters.length) {
          return {
            userChunks: userFilters.map((user) => [user]),
            clientChunks: chunk(clientFilters, chunkSize),
          };
        }

        return {
          userChunks: chunk(userFilters, chunkSize),
          clientChunks: clientFilters.map((client) => [client]),
        };
      }

      return {
        userChunks: size(userFilters) > 0 ? chunk(userFilters, chunkSize) : [[]],
        clientChunks: size(clientFilters) > 0 ? chunk(clientFilters, chunkSize) : [[]],
      };
    },

    /**
     * Reads a filter's selected value(s) out of the filters state. The shape depends on the
     * filter type: StringInArray filters (user/client/collection) return an array of ids,
     * while StringMatch filters (resourceId) return a single string. Returns `[]` when unset.
     *
     * @param {string} filterKey
     * @return {Array<string> | string}
     */
    getFilterItems(filterKey) {
      return chain(this.filters)
        .get([filterKey, 'filterStatements'])
        .head()
        .get('filteredItems', [])
        .value();
    },

    /**
     * Appends a user/client constraint: `==` for a single item, `in` for multiple.
     *
     * @param {Array<Constraint>} constraints Constraints array, mutated in place.
     * @param {string} field
     * @param {Array<string>} items
     * @return {void}
     */
    addChunkConstraint(constraints, field, items) {
      if (isEmpty(items)) return;
      if (size(items) === 1) {
        constraints.push([field, '==', items[0]]);
      } else {
        constraints.push([field, 'in', items]);
      }
    },

    /**
     * Runs parallel queries for all user/client chunk combinations and merges the results.
     *
     * @param {Array<Constraint>} baseConstraints Constraints shared by every query.
     * @param {string[][]} userChunks
     * @param {string[][]} clientChunks
     * @param {boolean} reset
     * @return {Promise<Object[]>}
     */
    async executeChunkedQueries(baseConstraints, userChunks, clientChunks, reset) {
      const auditLogDb = new AuditLogDB();
      const isSingleQuery = size(userChunks) <= 1 && size(clientChunks) <= 1;

      const queryPromises = [];
      userChunks.forEach((userChunk, ui) => {
        clientChunks.forEach((clientChunk, ci) => {
          // chunkKey tracks pagination cursor for each query combination
          const chunkKey = isSingleQuery ? 'single' : `${ui}-${ci}`;
          queryPromises.push(
            this.executeSingleQuery(auditLogDb, baseConstraints, userChunk, clientChunk, chunkKey, reset),
          );
        });
      });

      const results = await Promise.all(queryPromises);
      this.hasMoreDocs = results.some((r) => r.hasMore);

      if (isSingleQuery && results[0]) {
        this.lastDoc = this.lastDocs.single;
      }

      return this.mergeResults(results);
    },

    /**
     * Runs a single paginated query for one user/client chunk combination and advances its cursor.
     *
     * @param {AuditLogDB} auditLogDb
     * @param {Array<Constraint>} baseConstraints
     * @param {string[]} userChunk
     * @param {string[]} clientChunk
     * @param {string} chunkKey Identifies this combination's pagination cursor in `lastDocs`.
     * @param {boolean} reset
     * @return {Promise<{ docs: Object[], hasMore: boolean }>}
     */
    async executeSingleQuery(auditLogDb, baseConstraints, userChunk, clientChunk, chunkKey, reset) {
      const constraints = [...baseConstraints];
      this.addChunkConstraint(constraints, 'user', userChunk);
      this.addChunkConstraint(constraints, 'client', clientChunk);

      // use stored lastDoc for pagination ("load more" functionality)
      const lastDoc = reset ? null : get(this.lastDocs, chunkKey, null);
      const auditLogRef = await auditLogDb.readPaginated(constraints, 'createTimestamp', 'desc', this.maxRows, lastDoc);

      // only advance cursor if docs were returned; preserve it for exhausted chunks to avoid re-fetching
      const lastDocInRef = chain(auditLogRef).get('docs').last().value();
      if (lastDocInRef) {
        this.lastDocs[chunkKey] = markRaw(lastDocInRef);
      }

      return {
        // markRaw keeps Firestore data out of Vue's reactivity: rows are render-only, and deep-proxying
        // them makes every cell a tracked dependency of the table's virtual-scroll render effect
        docs: map(auditLogRef.docs, (doc) => markRaw({ ...doc.data(), id: doc.id })),
        hasMore: size(auditLogRef.docs) === this.maxRows,
      };
    },

    /**
     * Combines results from the parallel queries: flattens, de-duplicates by id, and
     * sorts by creation date (newest first).
     *
     * @param {Array<{ docs: Object[] }>} results
     * @return {Object[]}
     */
    mergeResults(results) {
      return chain(results)
        .flatMap('docs')
        .thru((docs) => uniqBy(docs, 'id'))
        .thru((docs) => orderBy(docs, [(doc) => doc.createTimestamp?.toMillis?.() || 0], ['desc']))
        .value();
    },

    async refreshUserCache(userIds) {
    // @todo Consider using users from Vuex store instead
      try {
        this.$q.loading.show();

        const readUserIds = chain(userIds)
          .uniq()
          .reject(isSystemActor)
          .reject((userId) => find(this.userCache, { userId }))
          .reject((userId) => includes(this.cacheReads, userId))
          .value();

        if (isEmpty(readUserIds)) return;

        // Make sure we don't start reading the same user multiple times
        this.cacheReads = concat(this.cacheReads, readUserIds);

        const usersDb = new UsersDB();
        const userActions = map(readUserIds, (userId) => usersDb.read(userId));

        const users = chain(await Promise.allSettled(userActions))
          .map((result) => (result.status === 'fulfilled' ? result.value : null))
          .compact()
          .value();

        const newUsers = chain(users)
          .map((user) => (pick(user, ['userId', 'name'])))
          .reject((user) => isEmpty(user.userId))
          .value();

        this.userCache.push(...newUsers);
        this.cacheReads = difference(this.cacheReads, readUserIds);
      } finally {
        this.$q.loading.hide();
      }
    },

    async refreshUserCacheFromTable(filteredAuditLog) {
      const userIds = chain(filteredAuditLog)
        .map('user')
        .uniq()
        .compact()
        .value();

      return this.refreshUserCache(userIds);
    }, // refreshUserCacheFromTable()

  },

};
</script>
<style lang="scss" scoped>
  .virtual-scroll-height {
    max-height: calc(90vh - 60px)
  }

</style>
